import React, { useEffect, useState } from "react";
import { parseISO, isValid, format as fmt } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from "axios";
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Table, 
  Tag, 
  Space, 
  Statistic,
  Badge,
  Checkbox,
  Tooltip,
  Spin
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FilterOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  TagOutlined,
  ReloadOutlined,
  EyeOutlined,
  ShopOutlined
} from '@ant-design/icons';
import moment from 'moment';

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData from "../../functions/fetchData";
import api from "../../api/api";
import logo from '../../assets/logo.png';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Helper function to get authentication headers
const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }
});

export default function SuppliersLedger() {
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [totals, setTotals] = useState({ credit: 0, debit: 0, balance: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectedTotals, setSelectedTotals] = useState({ debit: 0, credit: 0, balance: 0 });

  const [formData, setFormData] = useState({
    from: "",
    to: "",
    accounttype: "",
    suppliername: "",
    supplier_id: "",
    status: "all",
    showOnlyFinalBalance: false
  });

  const quickFilters = [
    { key: 'all', label: 'All Records', icon: EyeOutlined, color: 'default' },
    { key: 'pending', label: 'Pending', icon: ClockCircleOutlined, color: 'warning' },
    { key: 'completed', label: 'Completed', icon: CheckCircleOutlined, color: 'success' },
    { key: 'overdue', label: 'Overdue', icon: ExclamationCircleOutlined, color: 'error' }
  ];

  const accountTypes = [
    { value: '', label: 'All Account Types' },
    { value: 'Account Payable', label: '💰 Account Payable' },
    { value: 'Purchase', label: '🛒 Purchase' },
    { value: 'Cash', label: '💵 Cash' },
    { value: 'Expense', label: '💸 Expense' }
  ];

  const formatDateSafe = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "yyyy-MM-dd") : "";
  };

  const formatDisplayDate = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "dd/MM/yyyy") : "";
  };

  const toNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'string') {
      const normalized = value.replace(/,/g, '').trim();
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const resetFilters = () => {
    setFormData({
      from: "",
      to: "",
      accounttype: "",
      suppliername: "",
      supplier_id: "",
      status: "all",
      showOnlyFinalBalance: false
    });
    setQuickFilter('all');
    setSearchTerm('');
    setCurrentPage(1);
    setSelectedRecords([]);
    setData(allData);
  };

  // Handle checkbox selection
  const handleCheckboxChange = (record) => {
    const isSelected = selectedRecords.some(r => r.id === record.id);
    let newSelectedRecords;
    
    if (isSelected) {
      newSelectedRecords = selectedRecords.filter(r => r.id !== record.id);
    } else {
      newSelectedRecords = [...selectedRecords, record];
    }
    
    setSelectedRecords(newSelectedRecords);
    
    // Calculate totals for selected records
    const debit = newSelectedRecords.reduce((sum, r) => sum + toNumber(r.debit_amount), 0);
    const credit = newSelectedRecords.reduce((sum, r) => sum + toNumber(r.credit_amount), 0);
    setSelectedTotals({ debit, credit, balance: credit - debit });
  };

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRecords([...currentRecords]);
      const debit = currentRecords.reduce((sum, r) => sum + toNumber(r.debit_amount), 0);
      const credit = currentRecords.reduce((sum, r) => sum + toNumber(r.credit_amount), 0);
      setSelectedTotals({ debit, credit, balance: credit - debit });
    } else {
      setSelectedRecords([]);
      setSelectedTotals({ debit: 0, credit: 0, balance: 0 });
    }
  };

  // Mark selected records as completed
  const handleMarkAsCompleted = async () => {
    if (selectedRecords.length === 0) {
      toast.warning('Please select at least one record');
      return;
    }

    try {
      // Get current user ID from localStorage or sessionStorage
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      const userId = userStr ? JSON.parse(userStr).id : (localStorage.getItem('uname') || sessionStorage.getItem('uname'));
      
      if (!userId) {
        toast.error('User ID not found. Please log in again.');
        return;
      }

      // Update each selected record and create history entry
      const updatePromises = selectedRecords.map(async (record) => {
        // Update the ledger entry status
        await axios.put(
          `${process.env.REACT_APP_API_URL}/updatedata/supplier_ledger_entries`,
          {
            updatedFields: {
              status: 'completed',
              updated_by_id: userId
            },
            where: {
              id: record.id
            }
          },
          getHeaders()
        );

        // Create history record
        await axios.post(
          `${process.env.REACT_APP_API_URL}/insertdata/ledger_status_history`,
          {
            ledger_type: 'supplier',
            ledger_entry_id: record.id,
            previous_status: record.status || 'pending',
            new_status: 'completed',
            changed_by_id: userId
          },
          getHeaders()
        );
      });

      await Promise.all(updatePromises);
      
      // Reload data
      const fetched = await fetchData("supplier_ledger_entries", null, "id", {});
      setAllData(fetched);
      calculateTotals(fetched);
      
      // Clear selections
      setSelectedRecords([]);
      setSelectedTotals({ debit: 0, credit: 0, balance: 0 });
      
      toast.success(`Successfully marked ${updatePromises.length} record(s) as completed`);
    } catch (error) {
      console.error('Error updating records:', error);
      toast.error('Failed to update records');
    }
  };

  // Revert selected completed records back to pending
  const handleRevertCompleted = async () => {
    if (selectedRecords.length === 0) {
      toast.warning('Please select at least one record');
      return;
    }

    // Check if all selected records are completed
    const allCompleted = selectedRecords.every(r => r.status === 'completed');
    if (!allCompleted) {
      toast.warning('Can only revert records with completed status');
      return;
    }

    try {
      // Get current user ID from localStorage or sessionStorage
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      const userId = userStr ? JSON.parse(userStr).id : (localStorage.getItem('uname') || sessionStorage.getItem('uname'));
      
      if (!userId) {
        toast.error('User ID not found. Please log in again.');
        return;
      }

      // Revert each selected record
      const revertPromises = selectedRecords.map(async (record) => {
        // Update the ledger entry status back to pending
        await axios.put(
          `/updatedata/supplier_ledger_entries`,
          {
            updatedFields: {
              status: 'pending',
              updated_by_id: userId
            },
            where: {
              id: record.id
            }
          },
          getHeaders()
        );

        // Mark history records as reverted
        await axios.put(
          `/updatedata/ledger_status_history`,
          {
            updatedFields: {
              reverted: 1,
              reverted_at: new Date().toISOString(),
              reverted_by_id: userId
            },
            where: {
              ledger_type: 'supplier',
              ledger_entry_id: record.id,
              new_status: 'completed',
              reverted: 0
            }
          },
          getHeaders()
        );

        // Create new history record for the revert action
        await axios.post(
          `/insertdata/ledger_status_history`,
          {
            ledger_type: 'supplier',
            ledger_entry_id: record.id,
            previous_status: 'completed',
            new_status: 'pending',
            changed_by_id: userId
          },
          getHeaders()
        );
      });

      await Promise.all(revertPromises);
      
      // Reload data
      const fetched = await fetchData("supplier_ledger_entries", null, "id", {});
      setAllData(fetched);
      calculateTotals(fetched);
      
      // Clear selections
      setSelectedRecords([]);
      setSelectedTotals({ debit: 0, credit: 0, balance: 0 });
      
      toast.success(`Successfully reverted ${revertPromises.length} record(s) to pending`);
    } catch (error) {
      console.error('Error reverting records:', error);
      toast.error('Failed to revert records');
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF('landscape'); // Use landscape for better table width
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 10;
    
    // Header Section with soft light background
    doc.setFillColor(246, 244, 239);
    doc.rect(0, 0, pageWidth, 24, 'F');
    
    // Add Logo (left side)
    try {
      const logoWidth = 35;
      const logoHeight = 15;
      doc.addImage(logo, 'PNG', 10, 5, logoWidth, logoHeight);
    } catch (error) {
      console.error('Error adding logo:', error);
    }
    
    // Company/Report Title (center)
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('SUPPLIER LEDGER REPORT', pageWidth / 2, 13, { align: 'center' });
    
    // Generated timestamp with day (right side)
    const now = new Date();
    const dayName = fmt(now, 'EEEE'); // Full day name (e.g., Monday)
    const dateStr = fmt(now, 'dd/MM/yyyy');
    const timeStr = fmt(now, 'HH:mm:ss');
    
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated: ${dayName}`, pageWidth - 14, 10, { align: 'right' });
    doc.text(`${dateStr} ${timeStr}`, pageWidth - 14, 15, { align: 'right' });
    currentY = 28;
    
    // Reset text color for body
    doc.setTextColor(0, 0, 0);
    
    // Report Filters Section
    if (formData.suppliername || formData.from || formData.to || formData.accounttype) {
      doc.setFillColor(242, 245, 249);
      doc.rect(10, currentY, pageWidth - 20, 14, 'F');
      doc.setDrawColor(217, 223, 233);
      doc.rect(10, currentY, pageWidth - 20, 14, 'S');
      
      currentY += 5;
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('Report Filters:', 14, currentY);
      
      let filterText = [];
      if (formData.suppliername) filterText.push(`Supplier: ${formData.suppliername}`);
      if (formData.accounttype) filterText.push(`Account Type: ${formData.accounttype}`);
      if (formData.from && formData.to) {
        filterText.push(`Period: ${formatDisplayDate(formData.from)} to ${formatDisplayDate(formData.to)}`);
      }
      
      doc.setFont(undefined, 'normal');
      doc.text(filterText.join(' | '), 50, currentY);
      currentY += 12;
    }
    
    // Table columns
    const tableColumn = ["Txn ID", "Date", "Account Type", "Description", "Debit (THB)", "Credit (THB)", "Balance (THB)"];
    const tableRows = [];

    let runningBalance = 0;
    data.forEach((row) => {
      const debit = Number(row.debit_amount) || 0;
      const credit = Number(row.credit_amount) || 0;
      runningBalance += credit - debit;
      
      tableRows.push([
        row.transaction_id || '-',
        formatDisplayDate(row.date),
        row.account_type || '-',
        row.description || '-',
        debit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        credit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        runningBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      ]);
    });

    // Add totals row
    tableRows.push([
      { content: "TOTAL", colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
      totals.debit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      totals.credit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      totals.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: currentY,
      theme: "grid",
      headStyles: { 
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        lineWidth: 0.1,
        lineColor: [255, 255, 255]
      },
      styles: { 
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [200, 200, 200],
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 25, halign: 'center' },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 35 },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' },
        6: { cellWidth: 35, halign: 'right' }
      },
      didParseCell: function(data) {
        // Style the totals row
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [240, 248, 255];
          data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 9;
          data.cell.styles.lineWidth = 0.5;
          data.cell.styles.lineColor = [41, 128, 185];
        }
      },
      didDrawPage: function(data) {
        // Footer with page numbers
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(128);
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.text(
            `Page ${i} of ${pageCount}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      }
    });

    doc.save(`supplier_ledger_${fmt(new Date(), "ddMMyyyy_HHmmss")}.pdf`);
    toast.success("PDF exported successfully!");
  };

  const exportToExcel = () => {
    try {
      // Prepare data for Excel export
      const excelData = [];
      
      // Add header information
      excelData.push(['SUPPLIER LEDGER REPORT']);
      excelData.push([]);
      excelData.push([`Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`]);
      
      // Add filter information if applicable
      if (formData.suppliername || formData.from || formData.to || formData.accounttype) {
        excelData.push([]);
        excelData.push(['Report Filters:']);
        if (formData.suppliername) excelData.push([`Supplier: ${formData.suppliername}`]);
        if (formData.accounttype) excelData.push([`Account Type: ${formData.accounttype}`]);
        if (formData.from && formData.to) {
          excelData.push([`Period: ${formatDisplayDate(formData.from)} to ${formatDisplayDate(formData.to)}`]);
        }
      }
      
      excelData.push([]);
      
      // Add table headers
      const headers = ['Txn ID', 'Date', 'Supplier Name', 'Account Type', 'Description', 'Debit (THB)', 'Credit (THB)', 'Balance (THB)'];
      excelData.push(headers);
      
      // Add data rows
      let runningBalance = 0;
      data.forEach((row) => {
        const debit = Number(row.debit_amount) || 0;
        const credit = Number(row.credit_amount) || 0;
        runningBalance += credit - debit;
        
        const supplierName = row.supplier_name || 
          suppliers.find(s => s.id === row.supplier_id)?.supplier_name || 
          suppliers.find(s => s.id === row.supplier_id)?.name || 
          'Unknown Supplier';
        
        excelData.push([
          row.transaction_id || '-',
          formatDisplayDate(row.date),
          supplierName,
          row.account_type || '-',
          row.description || '-',
          debit.toFixed(2),
          credit.toFixed(2),
          runningBalance.toFixed(2)
        ]);
      });
      
      // Add totals row
      const totalRow = ['', '', '', '', 'TOTAL', totals.debit.toFixed(2), totals.credit.toFixed(2), totals.balance.toFixed(2)];
      excelData.push(totalRow);
      
      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Supplier Ledger');
      
      // Set column widths
      const colWidths = [
        { wch: 15 }, // Txn ID
        { wch: 12 }, // Date
        { wch: 20 }, // Supplier Name
        { wch: 18 }, // Account Type
        { wch: 30 }, // Description
        { wch: 15 }, // Debit
        { wch: 15 }, // Credit
        { wch: 15 }  // Balance
      ];
      ws['!cols'] = colWidths;
      
      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `supplier_ledger_${fmt(new Date(), "ddMMyyyy_HHmmss")}.xlsx`);
      
      toast.success("Excel exported successfully!");
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error("Failed to export Excel file");
    }
  };

  const exportCSVData = [
    // Add header information rows
    ...(formData.suppliername ? [
      { 
        transaction_id: 'Supplier Ledger Report',
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: `Supplier: ${formData.suppliername}`,
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: `Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`,
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: '',
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      }
    ] : [
      { 
        transaction_id: 'Supplier Ledger Report',
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: `Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`,
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      },
      { 
        transaction_id: '',
        date: '',
        supplier_name: '',
        account_type: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      }
    ]),
    // Add data rows
    ...data.map((row, index) => {
      let runningBalance = 0;
      data.slice(0, index + 1).forEach(r => {
        runningBalance += toNumber(r.credit_amount) - toNumber(r.debit_amount);
      });
      
      return {
        transaction_id: row.transaction_id,
        date: formatDisplayDate(row.date),
        supplier_name: row.supplier_name || 'N/A',
        account_type: row.account_type,
        description: row.description,
        debit_amount: row.debit_amount,
        credit_amount: row.credit_amount,
        running_balance: runningBalance.toFixed(2)
      };
    })
  ];

  const calculateTotals = (records) => {
    const credit = records.reduce((sum, r) => sum + toNumber(r.credit_amount), 0);
    const debit = records.reduce((sum, r) => sum + toNumber(r.debit_amount), 0);
    setData(records);
    setTotals({ credit, debit, balance: credit - debit }); // For suppliers: credit - debit
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "supplier_id") {
      const selected = suppliers.find((s) => s.id.toString() === value);
      setFormData((prev) => ({
        ...prev,
        supplier_id: value,
        suppliername: selected ? (selected.supplier_name || selected.name) : "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const getStatusBadge = (record) => {
    // Check if status is set in the database
    if (record.status === 'completed') {
      return <Tag color="success" icon={<CheckCircleOutlined />}>Completed</Tag>;
    }
    
    // Otherwise calculate based on balance
    const balance = (Number(record.credit_amount) || 0) - (Number(record.debit_amount) || 0);
    const recordDate = parseISO(record.date);
    const today = new Date();
    const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
    
    if (balance <= 0) {
      return <Tag color="success" icon={<CheckCircleOutlined />}>Completed</Tag>;
    } else if (daysDiff > 30) {
      return <Tag color="error" icon={<ExclamationCircleOutlined />}>Overdue</Tag>;
    } else {
      return <Tag color="warning" icon={<ClockCircleOutlined />}>Pending</Tag>;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const fetched = await fetchData("supplier_ledger_entries", null, "id", {});
        setAllData(fetched);
        calculateTotals(fetched);
        
        const suppliersData = await fetchData("supplier", null, "id", {});
        setSuppliers(suppliersData);
      } catch (e) {
        console.error("Error fetching data:", e);
        toast.error("Failed to load data");
      }
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    const filterData = () => {
      const { from, to, accounttype, supplier_id, status, showOnlyFinalBalance } = formData;
      let filtered = [...allData];

      // Apply search term filter
      if (searchTerm) {
        filtered = filtered.filter(r => 
          r.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.account_type?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Date range filter
      const safeFrom = formatDateSafe(from);
      const safeTo = formatDateSafe(to);
      if (safeFrom && safeTo) {
        filtered = filtered.filter((r) => {
          const d = formatDateSafe(r.date);
          return d && d >= safeFrom && d <= safeTo;
        });
      } else if (safeFrom) {
        filtered = filtered.filter((r) => {
          const d = formatDateSafe(r.date);
          return d && d >= safeFrom;
        });
      } else if (safeTo) {
        filtered = filtered.filter((r) => {
          const d = formatDateSafe(r.date);
          return d && d <= safeTo;
        });
      }

      // Account type filter
      if (accounttype && accounttype !== '') {
        filtered = filtered.filter((r) => r.account_type === accounttype) ;
      }

      // Supplier filter
      if (supplier_id !== "" && supplier_id !== null && supplier_id !== undefined) {
        const selectedSupplierId = Number(supplier_id);
        filtered = filtered.filter((r) => Number(r.supplier_id) === selectedSupplierId);
      }

      // Status filter from Advanced Filters
      if (status && status !== 'all') {
        filtered = filtered.filter((r) => {
          const recordStatus = r.status || 'pending';
          return recordStatus === status;
        });
      }

      // Quick filter for status
      if (quickFilter !== 'all') {
        const today = new Date();
        filtered = filtered.filter(r => {
          const balance = toNumber(r.credit_amount) - toNumber(r.debit_amount);
          switch (quickFilter) {
            case 'pending':
              return balance > 0;
            case 'completed':
              return balance <= 0;
            case 'overdue':
              const recordDate = parseISO(r.date);
              const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
              return balance > 0 && daysDiff > 30;
            default:
              return true;
          }
        });
      }

      // Apply final balance filter if enabled
      if (showOnlyFinalBalance) {
        // Group by supplier (supplier_id) and calculate final balance for each
        const supplierBalances = {};
        filtered.forEach(r => {
          const supplierId = r.supplier_id;
          if (!supplierBalances[supplierId]) {
            // Find supplier name from suppliers array
            const supplierInfo = suppliers.find(s => s.id === parseInt(supplierId));
            supplierBalances[supplierId] = {
              ...r,
              supplier_name: supplierInfo?.name || r.supplier_name || 'Unknown Supplier',
              debit_amount: 0,
              credit_amount: 0,
              final_balance: 0
            };
          }
          supplierBalances[supplierId].debit_amount += toNumber(r.debit_amount);
          supplierBalances[supplierId].credit_amount += toNumber(r.credit_amount);
        });
        
        // Calculate final balance and create summary records
        filtered = Object.values(supplierBalances).map(supplier => ({
          ...supplier,
          final_balance: supplier.credit_amount - supplier.debit_amount,
          description: `Final Balance for ${supplier.supplier_name}`,
          transaction_id: 'BALANCE_SUMMARY'
        }));
        console.log('After final balance grouping:', filtered.length);
      }

      calculateTotals(filtered);
      setCurrentPage(1); // Reset to first page when filters change
    };

    filterData();
  }, [formData, allData, quickFilter, searchTerm]);

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = data.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(data.length / recordsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        pageNumbers.push(currentPage - 1);
        pageNumbers.push(currentPage);
        pageNumbers.push(currentPage + 1);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };

  // Table columns configuration
  const columns = [
    {
      title: (
        <Checkbox
          onChange={handleSelectAll}
          checked={selectedRecords.length === currentRecords.length && currentRecords.length > 0}
        />
      ),
      key: 'select',
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedRecords.some(r => r.id === record.id)}
          onChange={() => handleCheckboxChange(record)}
        />
      ),
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 150,
      sorter: (a, b) => {
        const aId = a.transaction_id || '';
        const bId = b.transaction_id || '';
        return aId.localeCompare(bId);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => formatDisplayDate(date),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'descend',
    },
    {
      title: 'Supplier Name',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      render: (name, record) => (
        <strong style={{ color: '#1890ff' }}>
          {name || 
           suppliers.find(s => s.id === record.supplier_id)?.supplier_name || 
           suppliers.find(s => s.id === record.supplier_id)?.name || 
           'Unknown Supplier'}
        </strong>
      ),
      sorter: (a, b) => {
        const aName = a.supplier_name || 
          suppliers.find(s => s.id === a.supplier_id)?.supplier_name || 
          suppliers.find(s => s.id === a.supplier_id)?.name || '';
        const bName = b.supplier_name || 
          suppliers.find(s => s.id === b.supplier_id)?.supplier_name || 
          suppliers.find(s => s.id === b.supplier_id)?.name || '';
        return aName.localeCompare(bName);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Account Type',
      dataIndex: 'account_type',
      key: 'account_type',
      width: 150,
      render: (type) => <Tag>{type}</Tag>,
      sorter: (a, b) => (a.account_type || '').localeCompare(b.account_type || ''),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Debit',
      dataIndex: 'debit_amount',
      key: 'debit_amount',
      width: 150,
      align: 'right',
      render: (amount) => `฿${Number(amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`,
      sorter: (a, b) => (Number(a.debit_amount) || 0) - (Number(b.debit_amount) || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Credit',
      dataIndex: 'credit_amount',
      key: 'credit_amount',
      width: 150,
      align: 'right',
      render: (amount) => `฿${Number(amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`,
      sorter: (a, b) => (Number(a.credit_amount) || 0) - (Number(b.credit_amount) || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (_, record) => getStatusBadge(record),
      sorter: (a, b) => {
        const getStatusValue = (record) => {
          if (record.status === 'completed') return 0;
          const balance = (Number(record.credit_amount) || 0) - (Number(record.debit_amount) || 0);
          if (balance <= 0) return 0;
          const recordDate = parseISO(record.date);
          const today = new Date();
          const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
          if (daysDiff > 30) return 2; // Overdue
          return 1; // Pending
        };
        return getStatusValue(a) - getStatusValue(b);
      },
      sortDirections: ['ascend', 'descend'],
    },
  ];

  return (
    <Layout>
      <Header title="🏢 Supplier Ledger Management" />
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={{ padding: '24px' }}>
        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Statistic
                title="Total Credit"
                value={totals.credit}
                precision={2}
                prefix="฿"
                valueStyle={{ color: '#059669', fontWeight: 600 }}
                suffix={<DownloadOutlined style={{ fontSize: 16 }} />}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Statistic
                title="Total Debit"
                value={totals.debit}
                precision={2}
                prefix="฿"
                valueStyle={{ color: '#dc2626', fontWeight: 600 }}
                suffix={<DownloadOutlined style={{ fontSize: 16 }} />}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: totals.balance >= 0 ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Statistic
                title="Net Balance"
                value={totals.balance}
                precision={2}
                prefix="฿"
                valueStyle={{ color: totals.balance >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}
                suffix={<DownloadOutlined style={{ fontSize: 16 }} />}
              />
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Statistic
                title="Total Records"
                value={data.length}
                valueStyle={{ color: '#2563eb', fontWeight: 600 }}
                suffix={<EyeOutlined style={{ fontSize: 16 }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Selected Records Summary */}
        {selectedRecords.length > 0 && (
          <Card style={{ marginBottom: 16, background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)', border: '1px solid #7dd3fc', boxShadow: '0 4px 12px rgba(56, 189, 248, 0.15)' }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space size="large">
                  <strong>📋 {selectedRecords.length} Record(s) Selected</strong>
                  <span>
                    Debit: <strong>฿{selectedTotals.debit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                  </span>
                  <span>
                    Credit: <strong>฿{selectedTotals.credit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                  </span>
                  <span>
                    Balance: <strong style={{ color: selectedTotals.balance >= 0 ? '#3f8600' : '#cf1322' }}>
                      ฿{selectedTotals.balance.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                    </strong>
                  </span>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button 
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleMarkAsCompleted}
                    disabled={selectedRecords.some(r => r.status === 'completed')}
                  >
                    Mark as Completed
                  </Button>
                  <Button 
                    icon={<ReloadOutlined />}
                    onClick={handleRevertCompleted}
                    disabled={!selectedRecords.every(r => r.status === 'completed')}
                  >
                    Revert to Pending
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedRecords([]);
                      setSelectedTotals({ debit: 0, credit: 0, balance: 0 });
                    }}
                  >
                    Clear Selection
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* Header with Search and Actions */}
        <Card style={{ marginBottom: 16, background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Space>
                <Badge count={data.length} showZero style={{ backgroundColor: '#52c41a' }}>
                  <h3 style={{ margin: 0 }}>Supplier Ledger</h3>
                </Badge>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <Row gutter={[8, 8]} justify="end">
                <Col flex="auto">
                  <Input
                    placeholder="Search By Transaction ID, Description..."
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col>
                  <Space>
                    <Button 
                      icon={<FilterOutlined />}
                      onClick={() => setShowFilters(!showFilters)}
                      type={showFilters ? 'primary' : 'default'}
                    >
                      Filters
                    </Button>
                    <Button 
                      icon={<FilePdfOutlined />}
                      onClick={exportToPDF}
                      danger
                    >
                      PDF
                    </Button>
                    <Button 
                      icon={<FileExcelOutlined />}
                      onClick={exportToExcel}
                      style={{ background: '#107c41', color: 'white', borderColor: '#107c41' }}
                    >
                      Excel
                    </Button>
                    <Button 
                      icon={<ReloadOutlined />}
                      onClick={resetFilters}
                    >
                      Reset
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <Card 
            title={
              <Space>
                <FilterOutlined />
                <span>Advanced Filters</span>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <CalendarOutlined /> From Date
                  </label>
                  <Input
                    type="date"
                    value={formData.from}
                    onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                  />
                </div>
              </Col>
              
              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <CalendarOutlined /> To Date
                  </label>
                  <Input
                    type="date"
                    value={formData.to}
                    onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                  />
                </div>
              </Col>
              
              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <TagOutlined /> Account Type
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={formData.accounttype}
                    onChange={(value) => setFormData({ ...formData, accounttype: value })}
                  >
                    {accountTypes.map((type, idx) => (
                      <Option key={idx} value={type.value}>{type.label}</Option>
                    ))}
                  </Select>
                </div>
              </Col>
              
              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <ShopOutlined /> Supplier
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={formData.supplier_id}
                    onChange={(value) => {
                      const selectedSupplier = suppliers.find(s => s.id === parseInt(value));
                      setFormData({
                        ...formData,
                        suppliername: selectedSupplier ? (selectedSupplier.supplier_name || selectedSupplier.name) : "",
                        supplier_id: value,
                      });
                    }}
                    showSearch
                    optionFilterProp="children"
                  >
                    <Option value="">All Suppliers</Option>
                    {suppliers.map((supplier, idx) => (
                      <Option key={idx} value={supplier.id}>
                        {supplier.supplier_name || supplier.name} {supplier.company_name ? `(${supplier.company_name})` : ''}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              
              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <CheckCircleOutlined /> Status
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={formData.status}
                    onChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <Option value="all">All Statuses</Option>
                    <Option value="pending">Pending Only</Option>
                    <Option value="completed">Completed Only</Option>
                  </Select>
                </div>
              </Col>
              
              <Col xs={24}>
                <Checkbox
                  checked={formData.showOnlyFinalBalance}
                  onChange={(e) => setFormData({ ...formData, showOnlyFinalBalance: e.target.checked })}
                >
                  <strong>💰 Show Only Final Balance</strong>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Display only one record per supplier with their total balance amount
                  </div>
                </Checkbox>
              </Col>
            </Row>
          </Card>
        )}

        

        {/* Data Table */}
        <Card 
          title={
            <Space>
              <span>Supplier Ledger Entries</span>
              <Tag color="blue">{data.length} entries found</Tag>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={currentRecords}
            loading={loading}
            rowKey={(record) => record.id}
            pagination={false}
            scroll={{ x: 1200 }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1} colSpan={5} align="right">
                    <strong>TOTALS:</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <strong>฿{totals.debit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <strong>฿{totals.credit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="center">
                    <strong>Balance: ฿{totals.balance.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          
          {/* Pagination Controls */}
          {!loading && data.length > 0 && (
            <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
              <Col>
                <Space>
                  <span>Show</span>
                  <Select 
                    value={recordsPerPage}
                    onChange={handleRecordsPerPageChange}
                    style={{ width: 80 }}
                  >
                    <Option value={10}>10</Option>
                    <Option value={25}>25</Option>
                    <Option value={50}>50</Option>
                    <Option value={100}>100</Option>
                    <Option value={data.length}>All</Option>
                  </Select>
                  <span>
                    entries (Showing {indexOfFirstRecord + 1}-{Math.min(indexOfLastRecord, data.length)} of {data.length})
                  </span>
                </Space>
              </Col>

              {totalPages > 1 && (
                <Col>
                  <Space>
                    <Button 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    
                    {getPageNumbers().map((pageNumber, idx) => (
                      pageNumber === '...' ? (
                        <span key={`ellipsis-${idx}`}>...</span>
                      ) : (
                        <Button 
                          key={pageNumber}
                          type={currentPage === pageNumber ? 'primary' : 'default'}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Button>
                      )
                    ))}
                    
                    <Button 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </Space>
                </Col>
              )}
            </Row>
          )}
        </Card>
      </div>
    </Layout>
  );
}